import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { CalendarDays, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useDeviceEditPermissions } from "@/hooks/use-device-edit-permissions";
import { useDeviceChangeLog } from "@/hooks/use-device-change-log";

const DAYS: { long: string; short: string }[] = [
  { long: "Monday", short: "Mon" },
  { long: "Tuesday", short: "Tue" },
  { long: "Wednesday", short: "Wed" },
  { long: "Thursday", short: "Thu" },
  { long: "Friday", short: "Fri" },
  { long: "Saturday", short: "Sat" },
  { long: "Sunday", short: "Sun" },
];

const DEFAULT_CSV = DAYS.map((d) => d.long).join(",");
const NONE_CSV = "none";

const parseCsv = (csv: string | undefined | null): Set<string> => {
  // Only fall back to "all days" when no value is stored at all.
  // "none" (or an empty string, legacy) is a real value meaning "no operational days".
  if (csv === null || csv === undefined || typeof csv !== "string") {
    return new Set(DAYS.map((d) => d.long));
  }
  if (csv.trim().toLowerCase() === NONE_CSV) return new Set<string>();
  const lookup = new Map(DAYS.map((d) => [d.long.toLowerCase(), d.long]));
  const result = new Set<string>();
  for (const part of csv.split(",")) {
    const match = lookup.get(part.trim().toLowerCase());
    if (match) result.add(match);
  }
  return result;
};

interface Props {
  device: any;
  deviceId: string;
  onUpdate: () => Promise<void> | void;
}

export const OperationalDaysCard = ({ device, deviceId, onUpdate }: Props) => {
  const { canEditDeviceConfig } = useDeviceEditPermissions();
  const { logChange } = useDeviceChangeLog();

  const initialCsv: string = device?.deviceOperationalDays ?? DEFAULT_CSV;
  const [selected, setSelected] = useState<Set<string>>(() => parseCsv(initialCsv));
  const [savedCsv, setSavedCsv] = useState<string>(initialCsv);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const incoming: string = device?.deviceOperationalDays ?? DEFAULT_CSV;
    setSelected(parseCsv(incoming));
    setSavedCsv(incoming);
  }, [device?.deviceOperationalDays]);

  const currentCsv = useMemo(
    () =>
      selected.size === 0
        ? NONE_CSV
        : DAYS.filter((d) => selected.has(d.long)).map((d) => d.long).join(","),
    [selected]
  );

  const normalizedSaved = savedCsv === "" ? NONE_CSV : savedCsv;
  const isDirty = currentCsv !== normalizedSaved;
  const disabled = !canEditDeviceConfig || isSaving;

  const toggle = (day: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(day);
      else next.delete(day);
      return next;
    });
  };

  const handleSave = async () => {
    if (!isDirty) return;
    setIsSaving(true);
    try {
      // 1. BuildShip updateDevice
      const params = new URLSearchParams();
      params.append("deviceID", deviceId);
      params.append("field", "deviceOperationalDays");
      params.append("value", currentCsv);
      logChange(deviceId, "deviceOperationalDays", currentCsv);
      const res = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params,
      });
      if (!res.ok) throw new Error("BuildShip update failed");

      // 2. Persist in Supabase
      const { error } = await supabase
        .from("device_details")
        .update({ device_operational_days: currentCsv })
        .eq("device_id", deviceId);
      if (error) throw error;

      setSavedCsv(currentCsv);
      await onUpdate?.();
      toast.success("Operational days updated");
    } catch (err) {
      console.error("Failed to save operational days:", err);
      toast.error("Failed to update operational days");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="border bg-muted/50">
      <CardHeader className="py-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-brand-500" />
          <CardTitle className="text-base">Operational Days</CardTitle>
          {!canEditDeviceConfig && <Lock className="h-3 w-3 text-muted-foreground ml-auto" />}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-7 gap-1">
          {DAYS.map((d) => {
            const checked = selected.has(d.long);
            return (
              <label
                key={d.long}
                className={`flex flex-col items-center gap-1 rounded-md border p-1.5 cursor-pointer text-[11px] ${
                  checked ? "bg-[#DFEF97] border-[#DFEF97] text-black" : "bg-background"
                } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <Checkbox
                  checked={checked}
                  disabled={disabled}
                  onCheckedChange={(v) => toggle(d.long, v === true)}
                />
                <span className="font-medium">{d.short}</span>
              </label>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] text-muted-foreground">
            Camera only captures images on selected days.
          </p>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={disabled || !isDirty}
            className="h-7 text-xs bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90"
          >
            {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};